import { userErrorMessage } from 'app/core/error/user-error';
import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { WifiSettingsService } from 'app/core/api/wifi-settings.service';
import { finalize, Subject, takeUntil } from 'rxjs';
import { isSafeToken, wifiTokenFromParams } from 'app/modules/shared/utils/third-party-data';

@Component({
    selector: 'app-wifi-settings',
    templateUrl: './wifi-settings.component.html',
    styleUrl: './wifi-settings.component.scss',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        TranslocoModule,
    ]
})
export class WifiSettingsComponent implements OnInit, OnDestroy {
    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    wifiPasswordForm: UntypedFormGroup;
    showAlert: boolean = false;
    token = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _formBuilder: UntypedFormBuilder,
        private wifiSettings: WifiSettingsService,
        private route: ActivatedRoute,
        private _router: Router,
        private _translocoService: TranslocoService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // The link comes from outside the app: the token has to be well-formed (BSI O.TrdP_7).
        // New links carry it in the fragment ("#wst="), which no server ever receives (BSI O.Source_11); links that
        // were sent earlier carry it in the query ("?wst=").
        const wst = this.route.snapshot.queryParams['wst'];
        this.token = wifiTokenFromParams(this.route.snapshot.fragment) ?? (isSafeToken(wst) ? wst : undefined);

        // The token is in memory now: take it off the address bar, so that it does not stay in the history or on screen
        if (wst !== undefined || this.route.snapshot.fragment) {
            this._router.navigate([], {
                relativeTo: this.route,
                queryParams: { wst: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }

        // Create the form
        this.initForm();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    initForm() {
        this.wifiPasswordForm = this._formBuilder.group({
            ssid: ['', [Validators.required]],
            password: ['', [Validators.minLength(8)]],
        });

        this.wifiPasswordForm.enable();
        // Reset the form
        this.wifiPasswordForm.reset();
    }

    save(): void {
        const formData = this.wifiPasswordForm.getRawValue();

        // Return if the form is invalid
        if (this.wifiPasswordForm.invalid) {
            return;
        }

        // Disable the form
        this.wifiPasswordForm.disable();

        // // Hide the alert
        this.showAlert = false;

        this.wifiSettings
            .setWifiPassword(this.token, formData)
            .pipe(
                finalize(() => {
                    // Re-enable the form
                    this.wifiPasswordForm.enable();

                    // Reset the form
                    this.wifiPasswordForm.reset();

                    // Show the alert
                    this.showAlert = true;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(
                () => {
                    // Set the alert
                    this.alert = {
                        type: 'success',
                        message: this.t(
                            'THANK-YOU-VERY-MUCH-FOR-SHARING-THE-CONFIGURATION'
                        ),
                    };
                },
                (error) => {
                    // Set the alert
                    this.alert = {
                        type: 'error',
                        message: userErrorMessage(error, (key) => this.t(key)),
                    };
                }
            );
    }

    t(key: string) {
        return this._translocoService.translate(key);
    }
}
